import { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { StoreKind } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { StoresPage } from "@/modules/stores/pages/StoresPage";
import { useStoreByIdQuery } from "@/modules/stores/services/queries/useStoreByIdQuery";

type StoreDetailPageProps = {
  locale: Locale;
  storeId: string;
  /**
   * The list this record was opened from (`/restaurants/:id` says so). A bare
   * `/stores/:id` — a deep link, a notification — learns it from the store.
   */
  storeKind?: StoreKind;
};

function toNumber(value: string | null, fallback: number) {
  const parsed = value ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * A store's details: its list, with the record's big drawer open over it (the
 * design moved the record out of a page of its own). Closing the drawer goes
 * back to the list with the same page, seller, search and tab.
 */
export function StoreDetailPage({ locale, storeId, storeKind }: StoreDetailPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Same query (and cache entry) as the drawer.
  const { data } = useStoreByIdQuery({ storeId: storeKind ? "" : storeId });
  const loadedKind = data?.store.storeKind;

  // A restaurant opened at `/stores/:id` belongs under Restaurants: same
  // record, the right list and sidebar entry under it.
  useEffect(() => {
    if (!storeKind && loadedKind === "RESTAURANT") {
      navigate(`/${locale}/admin/restaurants/${storeId}${location.search}`, { replace: true });
    }
  }, [loadedKind, locale, location.search, navigate, storeId, storeKind]);

  const kind: StoreKind = storeKind ?? (loadedKind === "RESTAURANT" ? "RESTAURANT" : "DEFAULT");
  return (
    <StoresPage
      locale={locale}
      limit={toNumber(searchParams.get("limit"), 20)}
      offset={toNumber(searchParams.get("offset"), 0)}
      sellerId={searchParams.get("sellerId")?.trim() || undefined}
      storeKind={kind}
      openStoreId={storeId}
    />
  );
}
